import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PerformanceContext = createContext();

export const usePerformance = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PerformanceProvider = ({ children }) => {
  const { user } = useAuth();
  const [reviewCycles, setReviewCycles] = useState([]);
  const [reviewTemplates, setReviewTemplates] = useState([]);
  const [performanceReviews, setPerformanceReviews] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Review Cycles
  const fetchReviewCycles = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const response = await axios.get(`${API}/review-cycles?${params.toString()}`);
      setReviewCycles(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch review cycles';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const createReviewCycle = async (cycleData) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/review-cycles`, cycleData);
      
      // Refresh cycles list
      await fetchReviewCycles();
      
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create review cycle';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const updateCycleStatus = async (cycleId, status) => {
    try {
      setError(null);
      await axios.put(`${API}/review-cycles/${cycleId}/status?new_status=${status}`);
      
      // Refresh cycles list
      await fetchReviewCycles();
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update cycle status';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Review Templates
  const fetchReviewTemplates = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/review-templates`);
      setReviewTemplates(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch review templates';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const createReviewTemplate = async (templateData) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/review-templates`, templateData);
      
      // Refresh templates list
      await fetchReviewTemplates();
      
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create review template';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Performance Reviews
  const fetchPerformanceReviews = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const response = await axios.get(`${API}/performance-reviews?${params.toString()}`);
      setPerformanceReviews(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch performance reviews';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const createPerformanceReview = async (reviewData) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/performance-reviews`, reviewData);
      
      // Refresh reviews list
      await fetchPerformanceReviews();
      
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create performance review';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const submitSelfAssessment = async (reviewId, assessmentData) => {
    try {
      setError(null);
      await axios.put(`${API}/performance-reviews/${reviewId}/self-assessment`, assessmentData);
      
      // Refresh reviews list
      await fetchPerformanceReviews();
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit self-assessment';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const submitManagerReview = async (reviewId, reviewData) => {
    try {
      setError(null);
      await axios.put(`${API}/performance-reviews/${reviewId}/manager-review`, reviewData);
      
      // Refresh reviews list
      await fetchPerformanceReviews();
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit manager review';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // 360-Degree Feedback
  const submitPeerFeedback = async (feedbackData) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/peer-feedback`, feedbackData);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit peer feedback';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const fetchPeerFeedback = async (reviewId) => {
    try {
      setError(null);
      const response = await axios.get(`${API}/performance-reviews/${reviewId}/peer-feedback`);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch peer feedback';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Analytics
  const fetchPerformanceAnalytics = async (filters = {}) => {
    try {
      setError(null);
      
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const response = await axios.get(`${API}/performance/analytics/summary?${params.toString()}`);
      setAnalytics(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to fetch performance analytics';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Helper functions
  const getReviewsByStatus = (status) => {
    return performanceReviews.filter(review => review.status === status);
  };

  const getMyReviews = () => {
    return performanceReviews.filter(review => 
      review.reviewee_id === user?.id || review.reviewer_id === user?.id
    );
  };

  const getActiveReviewCycles = () => {
    return reviewCycles.filter(cycle => cycle.status === 'active');
  };

  // Load initial data
  useEffect(() => {
    if (user) {
      fetchReviewCycles();
      fetchReviewTemplates();
      fetchPerformanceReviews();
      fetchPerformanceAnalytics();
    }
  }, [user]);

  const value = {
    // State
    reviewCycles,
    reviewTemplates,
    performanceReviews,
    analytics,
    loading,
    error,
    
    // Review Cycles
    fetchReviewCycles,
    createReviewCycle,
    updateCycleStatus,
    
    // Review Templates
    fetchReviewTemplates,
    createReviewTemplate,
    
    // Performance Reviews
    fetchPerformanceReviews,
    createPerformanceReview,
    submitSelfAssessment,
    submitManagerReview,
    
    // 360-Degree Feedback
    submitPeerFeedback,
    fetchPeerFeedback,
    
    // Analytics
    fetchPerformanceAnalytics,
    
    // Helper functions
    getReviewsByStatus,
    getMyReviews,
    getActiveReviewCycles
  };

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
};